// Транспорт own-ветки дебиторки: /api/recv-own/* → recv_own_api.py в ядре. ТОЛЬКО ЧТЕНИЕ.
//
// ЗАЧЕМ ОТДЕЛЬНЫЙ ФАЙЛ, ЕСЛИ ЕСТЬ api/hooks.ts. Зеркальная дебиторка ходит общим
// маршрутом смарт-процессов (POST /api/bx, crm.item.list) и меряется ЗАПИСЯМИ 134.
// Own-ветка читает свою схему (invoice + payment) и меряется СЧЕТАМИ — другая единица,
// другая форма ответа, другие оговорки. Складывать их в один файл значило бы однажды
// переиспользовать «покажи дебиторку» и не заметить, что считаешь другим источником.
//
// Разбор ответа продублирован из api/docs.ts намеренно: общего «api/base.ts» в проекте
// нет, и вводить его ради двадцати строк значило бы поставить всем разделам общую
// точку отказа.

import { useQuery } from '@tanstack/react-query'

import { CoreUnsupported, Unauthorized } from './client'
import type { ScopeNote } from './types'

const БАЗА = '/api/recv-own'

/** Сколько строк на странице. То же число, что PAGE у ядра. */
export const РАЗМЕР_СТРАНИЦЫ = 50

async function запрос<T>(путь: string): Promise<T> {
  const res = await fetch(БАЗА + путь, { credentials: 'same-origin' })
  if (res.status === 401) throw new Unauthorized()
  const текст = await res.text()
  const данные: unknown = текст ? JSON.parse(текст) : {}
  if (!res.ok) {
    const rec = (данные || {}) as Record<string, unknown>
    if (res.status === 403) throw new Error('раздел «Дебиторка» вам не открыт')
    // 400 — неверный фильтр. Пустая таблица читалась бы как «долгов нет», а за таким
    // ответом идут звонить клиенту.
    if (res.status === 400) throw new CoreUnsupported(String(rec.why ?? rec.error ?? ''))
    const msg = (rec.error as string) || (rec.detail as string) || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return данные as T
}

// ─────────────── формы ответов ядра (см. recv_own_api.py) ───────────────

export type OwnBucketKey = 'overdue' | 'today' | 'week' | 'later' | 'nodate'

export interface OwnBucket {
  bucket: OwnBucketKey
  label: string
  count: number
  /** Живой ноль: пустая корзина — 0, а не «нет данных». */
  sum: number
}

/** Число и сумма — форма всех оговорок сводки. */
export interface OwnCountSum {
  count: number
  sum: number
}

export interface OwnSummary {
  buckets: OwnBucket[]
  total: OwnCountSum
  notes: {
    /** Всего непогашенных счетов (долг + погашенные платежами без даты на счёте). */
    unpaid_invoices: number
    /** Счета ВООБЩЕ без платёжной записи 134 — зеркальная дебиторка их не видела. */
    unlinked: OwnCountSum
    /** Погашены платежами полностью, но дата оплаты на счёте не проставлена. */
    covered_unmarked: OwnCountSum
    /** Платежи без даты оплаты: ожидание денег, долг они НЕ гасят. */
    expected_payments: number
    no_company: OwnCountSum
    no_assigned: OwnCountSum
    non_rub_invoices: number
  }
  /** Есть только при сужении зоной: долг счетов без ответственного, невидимый зоне. */
  hidden_no_assigned?: OwnCountSum
  src: string
  _scope?: ScopeNote
}

export interface OwnRow {
  /** Номер Bitrix или `own-<свой id>` — у заведённых уже в своей схеме. */
  id: string
  own_id: number
  bitrix_id: number | null
  no: string | null
  issued_at: string | null
  due_at: string | null
  amount: number
  currency: string
  /** Сколько погашено платежами С ДАТОЙ. */
  paid_covered: number
  remainder: number
  bucket: OwnBucketKey
  /** Дней просрочки (>0 — срок в прошлом); null — срока нет. */
  overdue_days: number | null
  /** Есть ли у счёта хоть одна платёжная запись 134. */
  linked: boolean
  /** Ожидаемых платежей (без даты) на счёте. */
  expected_payments: number
  company_bitrix_id: number | null
  company_own_id: number | null
  company_title: string | null
  deal_bitrix_id: number | null
  deal_title: string | null
  assigned_bitrix_id: number | null
  assigned_name: string | null
}

export interface OwnList {
  rows: OwnRow[]
  total: number
  next: number | null
  limit: number
  start: number
  sort: string
  src: string
  _scope?: ScopeNote
}

export type OwnSort = 'due' | 'amount' | 'issued' | 'updated'

export interface OwnParams {
  company?: string
  assigned?: string
  bucket?: string
  sort?: OwnSort
  start?: number
}

function строкаЗапроса(п: OwnParams, поля: Array<keyof OwnParams>): string {
  const q = new URLSearchParams()
  for (const имя of поля) {
    const v = п[имя]
    if (v !== undefined && v !== '' && v !== 0) q.set(имя, String(v))
  }
  const s = q.toString()
  return s ? `?${s}` : ''
}

// `users=` здесь не передаётся НИКОГДА: зону подставляет BFF из прав, а клиентское
// значение он выбрасывает — иначе параметр зоны стал бы способом посмотреть чужое.

export const useRecvOwnSummary = (п: OwnParams, включён = true) =>
  useQuery({
    queryKey: ['recv-own', 'summary', п.company ?? '', п.assigned ?? ''],
    queryFn: () => запрос<OwnSummary>(
      '/summary' + строкаЗапроса(п, ['company', 'assigned'])),
    enabled: включён,
    staleTime: 60_000,
    retry: (n, e) => !(e instanceof CoreUnsupported) && n < 2,
  })

export const useRecvOwnList = (п: OwnParams, включён = true) =>
  useQuery({
    queryKey: ['recv-own', 'list', п],
    queryFn: () => запрос<OwnList>(
      '/list' + строкаЗапроса(п, ['company', 'assigned', 'bucket', 'sort', 'start'])),
    enabled: включён,
    staleTime: 60_000,
    retry: (n, e) => !(e instanceof CoreUnsupported) && n < 2,
  })
